// Shared ops config client.
//
// Mirrors the backend SystemConfig singleton (backend/models/SystemConfig.js).
// The Settings page edits it; the 3D map reads it. Backend is the source of
// truth; we keep a localStorage + in-memory cache so the map renderer can read
// values synchronously each frame, and broadcast `ops-config-changed` so open
// views refresh live after a save.

import { API_URL, apiFetch } from './api';

const CACHE_KEY = 'utms_ops_config';
export const OPS_CONFIG_EVENT = 'ops-config-changed';

// Keep in sync with DEFAULTS in backend/models/SystemConfig.js.
export const DEFAULT_OPS_CONFIG = {
  delivery: {
    cruiseAltitude: 50, navSpeed: 5, dropHeight: 15, ropeLength: 15,
    winchRate: 0.5, servoChannel: 9, servoPWM: 1100,
    depotLat: null, depotLon: null,
    serviceRadiusMeters: 2000, clearanceRadiusMeters: 10,
    minSeparationMeters: 25, minDescentBandMeters: 10,
  },
  dropTiming: {
    settleBandMeters: 1.5, settleDescentRateMps: 0.4, settleHoldSeconds: 2,
    postDropHoldSeconds: 2, winchArmProximityMeters: 8,
  },
  corridor: {
    maxHorizontalDeviationMeters: 10, maxVerticalDeviationMeters: 7,
    timeBufferSeconds: 30, safetyDistanceMeters: 16,
    deconflictEnabled: true, breachAction: 'alert',
    layersEnabled: true, layerCeilingMeters: 121.92, layerCount: 10,
    colorCruise: '#55b6f2', colorTakeoff: '#34d399', colorLand: '#fbbf24',
    opacityActive: 0.45, opacityPreview: 0.28,
  },
  safety: {
    batteryCriticalAction: 'alert',
  },
  display: {
    buildingClearanceMeters: 10, droneScaleMeters: 1.5,
    trailMaxPoints: 500, cameraBackDistanceMeters: 18,
  },
};

function isObj(v) { return v && typeof v === 'object' && !Array.isArray(v); }

function deepMerge(base, over) {
  const out = { ...base };
  if (!isObj(over)) return out;
  for (const k of Object.keys(over)) {
    out[k] = isObj(base[k]) && isObj(over[k]) ? deepMerge(base[k], over[k]) : over[k];
  }
  return out;
}

let memoryCache = null;

function readLocalCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return deepMerge(DEFAULT_OPS_CONFIG, JSON.parse(raw));
  } catch { /* ignore */ }
  return null;
}

function writeCache(config) {
  memoryCache = config;
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(config)); } catch { /* ignore */ }
}

// Synchronous best-effort read for the render loop: memory → localStorage → defaults.
export function getCachedOpsConfig() {
  if (memoryCache) return memoryCache;
  const local = readLocalCache();
  memoryCache = local || DEFAULT_OPS_CONFIG;
  return memoryCache;
}

// Fetch the authoritative config from the backend and refresh the cache.
export async function loadOpsConfig() {
  try {
    const res = await apiFetch(`${API_URL}/config`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.config) {
        const merged = deepMerge(DEFAULT_OPS_CONFIG, data.config);
        writeCache(merged);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(OPS_CONFIG_EVENT, { detail: merged }));
        }
        return merged;
      }
    }
  } catch { /* offline / unauthorized — fall back to cache */ }
  return getCachedOpsConfig();
}

// Persist a partial config (admin only). Returns the merged config on success.
export async function saveOpsConfig(partial) {
  const res = await apiFetch(`${API_URL}/config`, {
    method: 'PUT',
    body: JSON.stringify({ config: partial }),
  });
  if (!res.ok) {
    const msg = res.status === 403 ? 'Admin permission required to change ops config' : `Save failed (HTTP ${res.status})`;
    throw new Error(msg);
  }
  const data = await res.json();
  const merged = deepMerge(DEFAULT_OPS_CONFIG, data.config || {});
  writeCache(merged);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPS_CONFIG_EVENT, { detail: merged }));
  }
  return merged;
}
